import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Radio, Tv, Globe, Facebook, Instagram, MessageCircle,
  ExternalLink, Search, Signal, Disc3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/layout/Layout";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/uploads/") ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};

function ChannelCard({ channel, index }) {
  const isRadio = channel.type === "radio";
  const IconType = isRadio ? Radio : Tv;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="group"
    >
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-400 border border-gray-100 h-full flex flex-col" data-testid={`channel-card-${channel.channel_id}`}>
        {/* Header with logo */}
        <div className={`relative p-5 pb-4 ${isRadio ? "bg-gradient-to-br from-purple-50 to-violet-50" : "bg-gradient-to-br from-blue-50 to-sky-50"}`}>
          <div className="flex items-start gap-4">
            {channel.logo_url ? (
              <img
                src={getImageUrl(channel.logo_url)}
                alt={channel.name}
                className="w-16 h-16 rounded-xl object-cover shadow-sm border border-white"
              />
            ) : (
              <div className={`w-16 h-16 rounded-xl flex items-center justify-center shadow-sm ${isRadio ? "bg-gradient-to-br from-purple-500 to-violet-600" : "bg-gradient-to-br from-blue-500 to-sky-600"}`}>
                <IconType className="h-8 w-8 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge className={`text-[10px] ${isRadio ? "bg-purple-100 text-purple-700 border-purple-200" : "bg-blue-100 text-blue-700 border-blue-200"} border`}>
                  <IconType className="h-2.5 w-2.5 mr-1" />
                  {isRadio ? "Radio" : "TV"}
                </Badge>
                {channel.is_local && (
                  <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 border">
                    Local
                  </Badge>
                )}
              </div>
              <h3 className="font-bold text-gray-900 text-lg leading-tight truncate">
                {channel.name}
              </h3>
              {(channel.frequency || channel.channel_number) && (
                <p className={`text-sm font-semibold mt-0.5 ${isRadio ? "text-purple-600" : "text-blue-600"}`}>
                  {isRadio ? channel.frequency : `Canal ${channel.channel_number}`}
                </p>
              )}
            </div>
          </div>
          {/* Live indicator */}
          <div className="absolute top-4 right-4">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500 text-white text-[10px] font-bold uppercase tracking-wider">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
              En Vivo
            </span>
          </div>
        </div>

        {/* Description */}
        {channel.description && (
          <div className="px-5 pt-3">
            <p className="text-gray-500 text-sm line-clamp-2">{channel.description}</p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-auto p-5 pt-4 space-y-3">
          {/* Stream button */}
          <a
            href={channel.stream_url}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
            data-testid={`channel-listen-${channel.channel_id}`}
          >
            <Button className={`w-full rounded-xl h-11 font-semibold ${isRadio ? "bg-purple-600 hover:bg-purple-500" : "bg-blue-600 hover:bg-blue-500"} text-white`}>
              {isRadio ? (
                <>
                  <Disc3 className="h-4 w-4 mr-2" />
                  Escuchar en Vivo
                </>
              ) : (
                <>
                  <Signal className="h-4 w-4 mr-2" />
                  Ver en Vivo
                </>
              )}
              <ExternalLink className="h-3.5 w-3.5 ml-2 opacity-60" />
            </Button>
          </a>

          {/* Social links row */}
          {(channel.website || channel.facebook || channel.instagram || channel.whatsapp) && (
            <div className="flex items-center gap-2">
              {channel.website && (
                <a href={channel.website} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors" title="Sitio Web">
                  <Globe className="h-4 w-4" />
                </a>
              )}
              {channel.facebook && (
                <a href={channel.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 hover:bg-blue-100 transition-colors" title="Facebook">
                  <Facebook className="h-4 w-4" />
                </a>
              )}
              {channel.instagram && (
                <a href={channel.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-pink-50 flex items-center justify-center text-pink-600 hover:bg-pink-100 transition-colors" title="Instagram">
                  <Instagram className="h-4 w-4" />
                </a>
              )}
              {channel.whatsapp && (
                <a href={`https://wa.me/${channel.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center text-green-600 hover:bg-green-100 transition-colors" title="WhatsApp">
                  <MessageCircle className="h-4 w-4" />
                </a>
              )}
              {/* Tags */}
              {channel.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 ml-auto">
                  {channel.tags.slice(0, 2).map((tag, i) => (
                    <span key={i} className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">#{tag}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function EnVivoPage() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");

  useEffect(() => {
    fetchChannels();
    axios.post(`${API}/track-page-view?page=en-vivo`).catch(() => {});
  }, []);

  const fetchChannels = async () => {
    try {
      const response = await axios.get(`${API}/public/en-vivo`);
      setChannels(response.data.items || []);
    } catch (error) {
      console.error("Error fetching channels:", error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = filterType === "all" ? channels : channels.filter(ch => ch.type === filterType);
  const radios = channels.filter(ch => ch.type === "radio");
  const tvs = channels.filter(ch => ch.type === "tv");

  return (
    <Layout>
      {/* === HERO === */}
      <section className="relative overflow-hidden bg-gray-950 py-20 md:py-28" data-testid="en-vivo-hero">
        {/* Animated background */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        <div className="absolute top-0 left-1/3 w-[600px] h-[400px] bg-gradient-to-b from-purple-500/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[300px] bg-gradient-to-tl from-blue-500/10 to-transparent rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-sm mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              Transmisiones en vivo
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
              Radios y<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
                Canales de TV
              </span>
            </h1>
            <p className="text-gray-400 text-base sm:text-lg max-w-xl mx-auto">
              Sintoniza las radios y canales de televisi&oacute;n con transmisi&oacute;n en vivo de Choluteca y Honduras
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="flex justify-center gap-12 mt-12"
          >
            <div className="text-center">
              <Radio className="h-5 w-5 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl md:text-3xl font-bold text-white">{radios.length}</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Radios</p>
            </div>
            <div className="text-center">
              <Tv className="h-5 w-5 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl md:text-3xl font-bold text-white">{tvs.length}</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Canales TV</p>
            </div>
            <div className="text-center">
              <Signal className="h-5 w-5 text-red-400 mx-auto mb-2" />
              <p className="text-2xl md:text-3xl font-bold text-white">{channels.length}</p>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* === FILTER BAR === */}
      <section className="bg-white border-b sticky top-0 z-30" data-testid="en-vivo-filters">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 py-4">
            {[
              { id: "all", label: "Todos", icon: Signal },
              { id: "radio", label: "Radios", icon: Radio },
              { id: "tv", label: "Canales de TV", icon: Tv },
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => setFilterType(opt.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 border ${
                  filterType === opt.id
                    ? "bg-gray-900 text-white border-gray-900 shadow-md"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
                data-testid={`filter-${opt.id}`}
              >
                <opt.icon className="h-4 w-4" />
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* === CHANNELS GRID === */}
      <section className="py-10 md:py-14 bg-gray-50/50" data-testid="en-vivo-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm animate-pulse border border-gray-100">
                  <div className="p-5 bg-gray-50">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-xl bg-gray-200" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-16 bg-gray-200 rounded-full" />
                        <div className="h-5 w-40 bg-gray-200 rounded" />
                        <div className="h-3 w-20 bg-gray-200 rounded" />
                      </div>
                    </div>
                  </div>
                  <div className="p-5 space-y-3">
                    <div className="h-3 w-full bg-gray-200 rounded" />
                    <div className="h-11 w-full bg-gray-200 rounded-xl" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Signal className="h-10 w-10 text-gray-300" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {filterType === "all" ? "No hay canales disponibles" : filterType === "radio" ? "No hay radios disponibles" : "No hay canales de TV disponibles"}
              </h3>
              <p className="text-gray-500">
                Pronto agregaremos m&aacute;s opciones de transmisi&oacute;n en vivo
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((ch, i) => (
                <ChannelCard key={ch.channel_id} channel={ch} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AdBanner />
        </div>
      </section>

      {/* === CTA === */}
      <section className="relative py-20 overflow-hidden bg-gray-950">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff'%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        <div className="absolute top-0 left-0 w-[400px] h-[300px] bg-purple-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="w-14 h-14 bg-purple-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Radio className="h-7 w-7 text-purple-400" />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              &iquest;Tienes una radio o canal de TV?
            </h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              Cont&aacute;ctanos para agregar tu transmisi&oacute;n en vivo a nuestra plataforma y llegar a m&aacute;s audiencia
            </p>
            <a href="/contacto">
              <Button size="lg" className="bg-purple-600 hover:bg-purple-500 text-white text-lg px-8 py-6 rounded-2xl shadow-lg shadow-purple-900/30" data-testid="cta-contact-btn">
                Cont&aacute;ctanos
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
